import React from 'react';
import { styled } from '@mui/material/styles';
import { renderHTML } from '@agility/nextjs';
import Link from 'next/link';
import { KitButton } from 'components/common/cms-kit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useSession } from 'next-auth/react';
import Heading3 from 'components/common/subcomponents/Typography/Heading3';
import QuestionForm from 'components/common/subcomponents/QuestionForm';

const Frame = styled('div')({
  background: 'rgba(217, 217, 217, 0.3)',
  '@media screen and (min-width: 1300px)': {
    padding: '25px 0 50px 0',
  },
  '@media screen and (max-width: 1299px)': {
    padding: '25px 0 40px 0',
  },
  '@media screen and (max-width: 999px)': {
    padding: '20px 0 30px 0',
  },
  '@media screen and (max-width: 699px)': {
    padding: '20px 0 20px 0',
  },
  '@media screen and (max-width: 450px)': {
    padding: '15px 0 10px 0',
  },
});

const Container = styled('div')(({ $session }) => ({
  width: '90%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '@media screen and (min-width: 1300px)': {
    margin: '25px auto',
    flexDirection: 'row',
    height: $session?.user ? '550px' : '620px',
    paddingBottom: $session?.user ? '25px' : '80px',
  },
  '@media screen and (max-width: 1299px)': {
    padding: 0,
    margin: '20px auto',
    flexDirection: 'column',
    height: '1250px',
  },
  '@media screen and (max-width: 999px)': {
    padding: 0,
    margin: '1px auto',
    flexDirection: 'column',
    minHeight: '1150px',
    justifyContent: 'normal',
  },
  '@media screen and (max-width: 699px)': {
    padding: 0,
    margin: '5px auto',
    flexDirection: 'column',
    minHeight: '1000px',
    justifyContent: 'normal',
  },
  '@media screen and (max-width: 450px)': {
    padding: 0,
    margin: '5px auto',
    flexDirection: 'column',
    height: '300px',
    justifyContent: 'normal',
  },
}));

const TitleContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  '@media screen and (min-width: 1300px)': {
    margin: '20px auto',
    width: '400px',
  },
  '@media screen and (max-width: 1299px)': {
    margin: '5px auto',
    width: '320px',
  },
});

const VideoContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  '.video': {
    aspectRatio: '16 / 9',
  },
  '&:after': {
    borderTop: 'solid 17px transparent',
    borderRight: 'solid 17px transparent',
    borderLeft: `solid 17px ${theme.palette.primary.main}`,
    transform: 'translateX(-50%)',
    position: 'absolute',
    zIndex: 5,
    content: '""',
    top: '79.2%',
    left: '100.3%',
    height: 0,
    width: 0,
  },
  '@media screen and (min-width: 1300px)': {
    width: '630px',
    height: '386px',
  },
  '@media screen and (max-width: 1299px)': {
    display: 'none',
  },
}));

const MobileVideoContainer = styled('div')({
  position: 'relative',
  '.video': {
    aspectRatio: '16 / 9',
  },
  justifyContent: 'center',
  '@media screen and (min-width: 1300px)': {
    display: 'none',
  },
  '@media screen and (max-width: 1299px)': {
    margin: '3% 0 10px 0',
    width: '70%',
    height: 'auto',
  },
  '@media screen and (max-width: 999px)': {
    margin: '5% 0 10px 0',
    width: '100%',
    height: 'auto',
  },
});

const EmbedContainer = styled('div')({
  '@media screen and (min-width: 1300px)': {
    margin: '0 0 50px 0',
    width: '95%',
  },
  '@media screen and (max-width: 1299px)': {
    width: '110%',
  },
});

const FormContainer = styled('div')(({ $session }) => ({
  position: 'relative',
  top: $session?.user ? '30px' : '95px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '@media screen and (min-width: 1300px)': {
    width: '45%',
    minHeight: '525px',
    padding: '0 0 15px 0',
  },
  '@media screen and (max-width: 1299px)': {
    width: '100%',
    minHeight: '530px',
  },
  '@media screen and (max-width: 450px)': {
    width: '100%',
    height: '500px',
  },
}));

const AskTheTrainer = ({ module }) => {
  const { fields } = module;
  const theme = useTheme();
  const { data: session } = useSession();

  return (
    <Frame>
      <Box
        sx={{
          width: '100%',
          ml: 'auto',
          mr: 'auto',
          maxWidth: { sm: '540px', md: '768px', lg: '1180px', xl: '1180px' },
        }}
      >
        <TitleContainer>
          <Heading3 color="#000000" underline={true} uppercase={true}>
            Have A Question?
            <br />
            Ask The Trainer
          </Heading3>
        </TitleContainer>
        <Container $session={session}>
          <VideoContainer>
            {fields.videoEmbed && (
              <EmbedContainer
                className="prose max-w-full mx-auto"
                dangerouslySetInnerHTML={renderHTML(fields.videoEmbed)}
              />
            )}
            <Link href="ask-the-trainer">
              <KitButton
                style={{
                  backgroundColor: theme.palette.obsBlue,
                  padding: '10px 40px',
                  fontSize: '1.1875rem',
                  fontWeight: 500,
                  position: 'absolute',
                  left: '314.5px',
                  top: '318px',
                  borderRadius: 0,
                  margin: '4.5px 0 0 -1px',
                  flexDirection: 'row',
                }}
              >
                View All Q&A Videos{' '}
                <PlayArrowIcon style={{ margin: '0 0 3px 10px' }} />
              </KitButton>
            </Link>
          </VideoContainer>
          <MobileVideoContainer>
            {fields.videoEmbed && (
              <EmbedContainer
                className="prose max-w-full mx-auto"
                dangerouslySetInnerHTML={renderHTML(fields.videoEmbed)}
              />
            )}
            <KitButton
              round
              style={{
                backgroundColor: theme.palette.obsBlue,
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
                padding: '10px 40px',
                fontSize: '1.1875rem',
                fontWeight: 500,
                margin: '5px auto',
                flexDirection: 'column',
              }}
            >
              <Link href="ask-the-trainer" rel="noopener noreferrer">
                View All Q&A Videos{' '}
                <PlayArrowIcon style={{ margin: '0 0 3px 10px' }} />
              </Link>
            </KitButton>
          </MobileVideoContainer>
          <FormContainer $session={session}>
            <QuestionForm buttonColor={theme.palette.primary.light} />
          </FormContainer>
        </Container>
      </Box>
    </Frame>
  );
};

export default AskTheTrainer;
